import React, { useState, useRef, useMemo } from 'react';
import { notification } from 'tea-component';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import { useEditProjectDetailInfo } from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/TeamGameInformation/hooks/useInformation';
import TeamGameModal from './components/TeamGameModal';
import GameInformation from './components/GameInformation';
import TeamInformation from './components/TeamInformation';
import Loading from '@gp/components/Loading';
import { reportBtnOperationLog } from '@gp/utils/common';
import { formatEngineRes, formatengineOptionalRes } from '@gp/utils/renderers';
import './index.scss';
import useTeamGameInfo from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/TeamGameInformation/hooks/useTeamGameInfo';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import HistoryBubble from '@gp/pages/Pipeline/ProjectDetails/components/HistoryBubble';
import { isEmpty } from 'lodash-es';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import {
  useAutoSaveParams,
  EDIT_PAGE_KEY_MAP,
} from '@gp/hooks/useAutoSaveParams';
import { formatTechnicalsConditions } from '@gp/pages/Pipeline/ProjectDetails/Technical/components/EditTechnical/utils';
import {
  formatEngineOptionalRes,
  formatEngineOptionsRes,
} from '@gp/pages/Workspace/Product/utils';

const formatEngineReq = (engine?: any[]): any => {
  if (!Array.isArray(engine) || !engine.length) return [];
  return engine?.map((item: any) => item.id);
};

const TeamGameInformation = (props: any) => {
  const { loading, gameInformationData, iconList, refreshFun } = props;

  const teamGameModal = useRef<any>(null);
  const [visible, setVisible] = useState(false);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [initialParams, setInitialParams] = useState({});
  const [sumbitLoading, setSumbitLoading] = useState(false);

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_gameDetails_gameInformation}_${gameInformationData?.project_id}`;
  }, [gameInformationData]);

  const { runAsync } = useEditProjectDetailInfo('infoEdit');
  const { studioOptionList, condDetailOptions, engineOptionList } =
    useTeamGameInfo({
      projectId: gameInformationData?.project_id || '',
    });
  const technicalsConditions = formatTechnicalsConditions(engineOptionList);
  const close = useMemoizedFn(() => {
    setVisible(false);
  });

  const [engineOptions, setEngineOptions] = useState<any>([]);
  const [engineOptional, setEngineOptional] = useState<any>([]);

  useDeepCompareEffect(() => {
    if (Array.isArray(technicalsConditions?.engine)) {
      const engineList = technicalsConditions?.engine;
      setEngineOptions(formatEngineOptionsRes(engineList));
      setEngineOptional(formatEngineOptionalRes(engineList));
    }
  }, [technicalsConditions]);

  const handleConfirm = useMemoizedFn(async (value) => {
    try {
      const { mandate, engine = [] } = value || {};
      const { project_id: projectId } = gameInformationData;
      setSumbitLoading(true);
      const params = {
        ...value,
        project: gameInformationData?.project || '',
        // studio_entity_id: value?.studio?.split('/')?.[1] || value?.studio_entity_id || '',
        project_id: projectId,
        // studio: value?.studio?.split('/')?.[0] || '',
        // platform: formatPlatformsReq(value?.platform) || '',
        business_model: (value?.business_model as string[])?.join(';') || '',
        // genre: (value?.genre as string[])?.join(',') || '',
        // ip: (value?.ip as string[])?.join('/') || '',
        mandate:
          Array.isArray(mandate) && mandate?.length > 0 ? mandate[0] : {},
        engine: formatEngineRes(engine, engineOptional),
        engine_optional: formatengineOptionalRes(engineOptional),
      };
      await runAsync(params);
      refreshFun?.();
      notification.success({
        description: 'Save success',
        duration: 1000,
      });
      close();
      deleteSaveParamsByPageKey(pageKeyCur);
    } catch (error) {
      notification.error({
        description: 'Save failed. Please check the network',
        duration: 1000,
      });
    } finally {
      reportBtnOperationLog(
        'DETAIL_PROJECT_DETAIL_GAME_INFORMATION_EDIT_SUBMIT',
      );
      setSumbitLoading(false);
    }
  });

  const setTeamGameFormModel = (gameInformationData: any) => {
    let initValue = {
      studio_entity_id: gameInformationData?.studio_entity_id || '',
      publisher: gameInformationData?.publisher || '',
      studio_location: gameInformationData?.studio_location || '',
      ip: gameInformationData?.ip || [],
      genre: gameInformationData?.genre || [],
      platform: gameInformationData?.platform || [],
      business_model: gameInformationData?.business_model || [],
      engine: formatEngineReq(gameInformationData?.engine) || [],
      mandate: !isEmpty(gameInformationData?.mandate)
        ? [gameInformationData?.mandate]
        : [],
    } as any;
    teamGameModal?.current?.init(initValue);
    if (
      !isEmpty(gameInformationData?.mandate) &&
      !Array.isArray(gameInformationData?.mandate) &&
      gameInformationData?.mandate?.cloud_path
    ) {
      const mandate = gameInformationData?.mandate || {};
      const fileObj = {
        path: mandate?.cloud_path,
        name: mandate?.name,
        size: mandate?.file_size,
      };
      initValue = {
        ...initValue,
        mandate: [fileObj],
      };
    }
    setInitialParams(initValue);
  };

  const handleAutoSaveConfirm = () => {
    const saveValue = getSaveParamsValue(pageKeyCur) || [];
    teamGameModal?.current?.init(saveValue);
    setAutoSaveVisible(false);
    setVisible(true);
  };

  const handleAutoSaveCancel = () => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(pageKeyCur);
    teamGameModal?.current?.init(initialParams);
    setVisible(true);
  };

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } =
    useAutoSaveParams({
      pageKey: pageKeyCur,
    });

  const handleEditClick = useMemoizedFn(() => {
    // 如果有自动保存过的，先咨询
    if (showRestorePrompt(pageKeyCur)) {
      setAutoSaveVisible(true);
    } else {
      setVisible(true);
    }
  });

  return (
    <div className="dbg-project-detail-team-game-section">
      <CardSlot
        className="dbg-project-detail-team-game-section-team-information"
        title={
          <div className="dbg-project-detail_horizontal-section-title dbg-black">
            Team Information
            <HistoryBubble
              loading={loading}
              newTime={loading ? '-' : gameInformationData?.update_time}
            />
          </div>
        }
      >
        {loading ? (
          <Loading
            loading
            style={{ height: 170, padding: 10 }}
            className="dbg-abs-center"
          />
        ) : (
          <TeamInformation gameInformationData={gameInformationData} />
        )}
      </CardSlot>
      <CardSlot
        className="dbg-project-detail-team-game-section-game-information"
        title={
          <div className="dbg-project-detail_horizontal-section-title dbg-black">
            Game Information
            <HistoryBubble
              loading={loading}
              newTime={loading ? '-' : gameInformationData?.update_time}
            />
          </div>
        }
        getChange={(value: any) => {
          if (value === 'pencil-m') {
            setTeamGameFormModel(gameInformationData);
            handleEditClick();
          }
        }}
        iconList={iconList}
      >
        {loading ? (
          <Loading
            loading
            style={{ height: 170, padding: 10 }}
            className="dbg-abs-center"
          />
        ) : (
          <GameInformation gameInformationData={gameInformationData} />
        )}
      </CardSlot>

      {/* TeamGameModal 编辑弹窗 */}
      <TeamGameModal
        ref={teamGameModal}
        visible={visible}
        gameInformationData={gameInformationData}
        onConfirm={handleConfirm}
        studioOptionList={studioOptionList}
        condDetailOptions={condDetailOptions}
        technicalsConditions={technicalsConditions}
        engineOptions={engineOptions}
        engineOptional={engineOptional}
        setEngineOptional={setEngineOptional}
        sumbitLoading={sumbitLoading}
        pageKeyCur={pageKeyCur}
        initialParams={initialParams}
        onCancel={() => {
          teamGameModal?.current?.init(initialParams);
          deleteSaveParamsByPageKey(pageKeyCur);
          setVisible(!visible);
        }}
      />

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </div>
  );
};

export default TeamGameInformation;
